import { useMemo } from "react";

import type { AxiosRequestConfig, AxiosResponse } from "axios";
import axios from "axios";
import appConfig from "config/appConfig";

// import { setupCache } from "axios-cache-adapter";
import type { z } from "zod";

import { handleZodResolvers } from "utils/handleZodResolvers";

import ApiError from "./ApiError";

import { useRestrictedInterceptor, useUnAuthorizedInterceptor } from "../interceptors";

// const cache = setupCache({
// 	maxAge: 15 * 60 * 1000, // 15 mins
// 	exclude: {
// 		// Only exclude PUT, PATCH and DELETE methods from cache
// 		methods: ["put", "patch", "delete"]
// 	}
// });

interface OptionProps {
	customErrorHandlers?: any[];
}

interface AxiosZodOptions extends AxiosRequestConfig {
	resolvers?: {
		resolver: z.ZodNullable<any> | z.ZodOptional<any> | z.ZodObject<any> | z.ZodLazy<z.ZodObject<any>>;
		fields?: string[];
	}[];
}

const useHttpActions = (options?: OptionProps) => {
	const interceptUnAuthorized = useUnAuthorizedInterceptor();
	const interceptRestricted = useRestrictedInterceptor();

	return useMemo(() => {
		const errorInterceptors: any[] = options?.customErrorHandlers || [interceptUnAuthorized, interceptRestricted];

		const config: AxiosRequestConfig = {
			baseURL: appConfig.GLOBAL_CONSTANTS.BACKEND_API_URL,
			// timeout: 5000,
			withCredentials: true, // store cookie from the server
			validateStatus: status => status >= 200 && status < 300
			// adapter: cache.adapter
		};

		const request = axios.create(config);

		request.interceptors.response.use(
			response => {
				return response;
			},
			response => {
				if (response.response) {
					const { status, data } = response.response;
					const apiErrorInstance = new ApiError(status, data.errors || [], data.message);
					errorInterceptors.forEach(f => f(apiErrorInstance));
					throw apiErrorInstance;
				} else {
					throw Error(response.message);
				}
			}
		);
		return {
			get: async (url: string, params?: any, options?: AxiosZodOptions): Promise<any> => {
				try {
					const { resolvers, ...rest } = options || {};
					const response = await request.get(url, { params, ...rest });

					if (resolvers) return handleZodResolvers(resolvers, response);

					return response;
				} catch (error) {
					console.error(error);
				}
			},
			post: async <T>(url: string, formData: T, options?: AxiosZodOptions): Promise<any> => {
				try {
					const { resolvers, ...rest } = options || {};
					const response = await request.post(url, formData, rest);

					if (resolvers) return handleZodResolvers(resolvers, response);

					return response;
				} catch (error) {
					console.error(error);
				}
			},
			put: async <T>(url: string, formData: T, options?: AxiosZodOptions): Promise<any> => {
				try {
					const { resolvers, ...rest } = options || {};
					const response = await request.put(url, formData, rest);

					if (resolvers) return handleZodResolvers(resolvers, response);

					return response;
				} catch (error) {
					console.error(error);
				}
			},
			delete: async <T>(
				url: string,
				data?: any,
				params?: any,
				options?: AxiosZodOptions
			): Promise<AxiosResponse<T>> => {
				const config = { url, data, params, ...options };
				return request.delete(url, await config);
			},
			patch: async <T>(url: string, data: any, params?: any, options?: AxiosZodOptions): Promise<AxiosResponse<T>> => {
				return request.patch(url, data, { params, ...options });
			},
			getBlob: async (url: string, options?: AxiosZodOptions): Promise<string> => {
				return axios({
					url,
					method: "GET",
					responseType: "blob",
					...options
				}).then(response => window.URL.createObjectURL(new Blob([response.data], { type: "text/csv;charset=utf-8;" })));
			}
		};
	}, [options, interceptRestricted, interceptUnAuthorized]);
};

export default useHttpActions;
